import { Heading, HStack, PageContainer, Spread, Surface, Text, VStack } from '@/components/Atoms'
import { Badge } from '@/components/Badge'
import { Card, CardContent } from '@/components/Card'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import { Spinner } from '@/components/Spinner'
import {
  useMassClrLineComments,
  useMassClrRun,
  useMassClrRunLines,
  useSubmitMassClrRun,
  type MassClrChainPerson,
  type MassClrLine,
} from '@/hooks/queries/credit/useMassClr'
import { useLocale } from '@/hooks/useLocale'
import { formatDoaBandLabel } from '@/lib/utils/doaBands'
import { formatCurrency } from '@/lib/utils/numbers'
import { MASS_DOA_OPTION_KEY } from '@repo/constants'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { ArrowLeftIcon, ChevronRightIcon, SendIcon, TriangleAlertIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MassRunStatusBadge } from '../-components/MassRunStatusBadge'
import { formatMoneyDelta } from '../-components/massClrFormat'
import { computeRunStats, getMassClrLineState, preparerCommentedLineIds } from '../-data/lineState'
import { CREDIT_ROUTES, toStaticData } from '../../../credit-route-config'

export const Route = createFileRoute(
  '/_auth/_modules/credit/credit-limit-reviews/mass/$runId/submit',
)({
  component: RouteComponent,
  staticData: toStaticData(CREDIT_ROUTES['/credit/credit-limit-reviews/mass/$runId/submit']),
})

function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const locale = useLocale()
  const params = Route.useParams()
  const runId = Number(params.runId)
  const navigate = Route.useNavigate()

  const runQuery = useMassClrRun(runId)
  const run = runQuery.data
  // Owner-only, like the workbench: anyone else lands back on the run page (receipt/review).
  const isEditable =
    (run?.status === 'draft' || run?.status === 'sent_back_to_preparer') &&
    (run?.viewer.isPreparer ?? false)
  const linesQuery = useMassClrRunLines(runId, isEditable)
  const commentsQuery = useMassClrLineComments(runId, isEditable)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const submitMutation = useSubmitMassClrRun(runId)

  if (runQuery.isPending) return <PageLoader />
  if (!run) return <PageError />
  if (!isEditable) {
    return (
      <Navigate to="/credit/credit-limit-reviews/mass/$runId" params={{ runId: params.runId }} />
    )
  }
  if (linesQuery.isPending || !linesQuery.data || commentsQuery.isPending) return <PageLoader />

  const lines = linesQuery.data.lines
  const stats = computeRunStats(lines)

  // Every mass run runs the same chain at the same threshold, so this is a constant rather than
  // something derived from the run's lines.
  const bandLabel = formatDoaBandLabel(MASS_DOA_OPTION_KEY, t, locale)

  const deviationGroups = buildDeviationGroups(lines)
  // A hard gate (phase 2 UAT, second round): the server refuses the submission too.
  const uncommented = computeRunStats(
    lines,
    preparerCommentedLineIds(commentsQuery.data?.comments ?? []),
  ).counts.uncommented
  const isBlocked = uncommented > 0

  const submit = () => {
    setIsConfirmOpen(false)
    submitMutation.mutate(undefined, {
      onSuccess: () =>
        navigate({
          to: '/credit/credit-limit-reviews/mass/$runId',
          params: { runId: params.runId },
        }),
    })
  }

  return (
    <PageContainer maxWidth="wide">
      {/* Same header shape as the workbench: back navigation leads on the left. */}
      <VStack gap="sm">
        <HStack gap="sm" align="center" className="min-w-0 flex-wrap">
          <Button type="button" variant="ghost" size="sm" asChild>
            <Link to="/credit/credit-limit-reviews/mass/$runId" params={{ runId: params.runId }}>
              <ArrowLeftIcon />
              {t('massClr.submit.backToRun')}
            </Link>
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <Heading level={2}>{t('massClr.submit.pageHeader')}</Heading>
          <MassRunStatusBadge status={run.status} pendingSlot={run.pendingSlot} />
        </HStack>
        <Text tone="muted">
          {run.name} · <span className="font-mono">{run.reference}</span>
        </Text>
      </VStack>

      <Card>
        <CardContent className="pt-6">
          <VStack gap="md" align="stretch">
            <VStack gap="none">
              <Text weight="medium">{t('massClr.submit.aboutTitle')}</Text>
              <Text size="xs" tone="muted">
                {t('massClr.submit.aboutSub')}
              </Text>
            </VStack>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label={t('massClr.submit.stat.accounts')}
                value={stats.inRun.toLocaleString()}
                hint={
                  stats.counts.removed || stats.counts.withdrawn
                    ? t('massClr.submit.statHint.removed', {
                        removed: stats.counts.removed.toLocaleString(),
                        withdrawn: stats.counts.withdrawn.toLocaleString(),
                      })
                    : t('massClr.submit.statHint.noneRemoved')
                }
              />
              <Stat
                label={t('massClr.submit.stat.deviating')}
                value={stats.counts.deviated.toLocaleString()}
                hint={t('massClr.submit.statHint.distinctReasons', {
                  count: deviationGroups.length,
                })}
              />
              <Stat
                label={t('massClr.submit.stat.unchanged')}
                value={stats.counts.unchanged.toLocaleString()}
                hint={t('massClr.submit.statHint.noRecommendation')}
              />
              <Stat
                label={t('massClr.submit.stat.exposure')}
                value={formatMoneyDelta(locale, stats.exposureDelta)}
                valueTone={stats.exposureDelta > 0 ? 'warning-dark' : 'success-dark'}
                hint={`${formatCurrency(locale, stats.currentExposure)} → ${formatCurrency(locale, stats.proposedExposure)}`}
              />
            </div>
          </VStack>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <VStack gap="md" align="stretch">
            <VStack gap="none">
              <Text weight="medium">{t('massClr.submit.chainTitle')}</Text>
              <Text size="xs" tone="muted">
                {t('massClr.submit.chainSub', { band: bandLabel })}
              </Text>
            </VStack>

            <HStack gap="sm" align="center" className="flex-wrap">
              <Badge tone="success" className="px-3 py-1">
                {run.preparerName} · {t('delegationOfAuthority.slot.preparer')}
              </Badge>
              <ChevronRightIcon className="text-muted-foreground size-3.5" />
              <Badge tone="alpha" className="px-3 py-1">
                {t('delegationOfAuthority.slot.approver')}
              </Badge>
              <Text size="xs" tone="muted" as="span">
                {t('massClr.submit.chainDeviationsNote', { count: stats.counts.deviated })}
              </Text>
            </HStack>

            {/* No pre-selection (Aug 12 decision): submission notifies the eligible holder;
                whoever acts is recorded in the summary. */}
            <Surface tone="level-2" radius="md" className="border p-3">
              <Text size="xs" tone="muted" className="text-pretty">
                {t('massClr.submit.noPreselect')}
              </Text>
            </Surface>

            <Separator />

            <div className="grid gap-4 sm:grid-cols-2">
              <ChainSlotPanel
                label={t('massClr.submit.eligibleApprovers', {
                  count: run.chain.approvers.length,
                })}
                people={run.chain.approvers}
                bandLabel={bandLabel}
                description={t('massClr.submit.approverDesc')}
              />
            </div>
          </VStack>
        </CardContent>
      </Card>

      {isBlocked && (
        <Surface tone="warning" radius="md" className="flex w-full items-center gap-2.5 border p-3">
          <TriangleAlertIcon className="size-4 shrink-0" />
          <Text size="xs" className="flex-1 text-pretty">
            {t('massClr.submit.uncommentedWarning', { count: uncommented })}
          </Text>
          <Button type="button" variant="secondary" size="sm" asChild>
            <Link to="/credit/credit-limit-reviews/mass/$runId" params={{ runId: params.runId }}>
              {t('massClr.submit.keepWorking')}
            </Link>
          </Button>
        </Surface>
      )}

      {deviationGroups.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <VStack gap="md" align="stretch">
              <VStack gap="none">
                <Text weight="medium">{t('massClr.submit.deviationsTitle')}</Text>
                <Text size="xs" tone="muted">
                  {t('massClr.submit.deviationsSub')}
                </Text>
              </VStack>
              <div className="bg-background-level-1 w-full rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('massClr.submit.deviationsTable.reason')}</TableHead>
                      <TableHead className="text-right">
                        {t('massClr.submit.deviationsTable.lines')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('massClr.submit.deviationsTable.exposureChange')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deviationGroups.map((group) => (
                      <TableRow key={group.reason}>
                        <TableCell>
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Text size="xs" maxLines="two" className="cursor-default">
                                  {group.reason}
                                </Text>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                align="start"
                                className="max-w-md whitespace-pre-wrap"
                              >
                                {group.reason}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {group.count.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          <Text
                            size="xs"
                            as="span"
                            tone={group.exposureDelta > 0 ? 'warning-dark' : 'muted'}
                          >
                            {formatMoneyDelta(locale, group.exposureDelta)}
                          </Text>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </VStack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <Spread align="center" className="flex-wrap gap-4">
            <VStack gap="none">
              <Text weight="medium">{t('massClr.submit.footerTitle', { count: stats.inRun })}</Text>
              <Text size="xs" tone="muted" className="max-w-2xl text-pretty">
                {t('massClr.submit.footerSub')}
              </Text>
            </VStack>
            <HStack gap="sm">
              <Button type="button" variant="secondary" asChild>
                <Link
                  to="/credit/credit-limit-reviews/mass/$runId"
                  params={{ runId: params.runId }}
                >
                  {t('massClr.submit.keepWorking')}
                </Link>
              </Button>
              <Button
                type="button"
                disabled={submitMutation.isPending || isBlocked}
                onClick={() => setIsConfirmOpen(true)}
              >
                {submitMutation.isPending ? <Spinner /> : <SendIcon />}
                {t('massClr.submit.submit')}
              </Button>
            </HStack>
          </Spread>
        </CardContent>
      </Card>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('massClr.submit.dialogTitle', { count: stats.inRun })}</DialogTitle>
            <DialogDescription>{t('massClr.submit.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <VStack gap="sm" align="stretch">
            {[
              [t('massClr.submit.dialogAccounts'), stats.inRun.toLocaleString()],
              [t('massClr.submit.dialogDeviations'), stats.counts.deviated.toLocaleString()],
              [t('massClr.submit.dialogExposure'), formatMoneyDelta(locale, stats.exposureDelta)],
              [
                t('massClr.submit.dialogNotifies'),
                run.chain.approvers.length > 0
                  ? run.chain.approvers.map((person) => person.name).join(', ')
                  : t('massClr.receipt.eligibleNotified', { count: 0 }),
              ],
            ].map(([label, value]) => (
              <Spread key={label} align="center">
                <Text size="xs" tone="muted">
                  {label}
                </Text>
                <Text size="xs" monospace className="text-right">
                  {value}
                </Text>
              </Spread>
            ))}
          </VStack>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setIsConfirmOpen(false)}>
              {t('actions.cancel', { ns: 'common' })}
            </Button>
            <Button type="button" onClick={submit}>
              {t('massClr.submit.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}

function Stat(props: {
  label: string
  value: string
  hint?: string
  valueTone?: 'warning-dark' | 'success-dark'
}) {
  return (
    <VStack gap="xs" className="min-w-0">
      <Text size="xs" tone="muted" weight="medium">
        {props.label}
      </Text>
      <Text size="lg" weight="semibold" monospace tone={props.valueTone}>
        {props.value}
      </Text>
      {props.hint && (
        <Text size="xs" tone="muted">
          {props.hint}
        </Text>
      )}
    </VStack>
  )
}

/** The eligible holders for one slot — informational, nothing to pick. */
function ChainSlotPanel(props: {
  label: string
  people: MassClrChainPerson[]
  bandLabel: string
  description: string
}) {
  const { t } = useTranslation('credit')
  return (
    <VStack gap="sm" align="stretch">
      <Text size="xs" weight="medium">
        {props.label}
      </Text>
      <VStack gap="xs" align="stretch" className="rounded-md border p-3">
        {props.people.length === 0 ? (
          <Text size="xs" tone="muted">
            —
          </Text>
        ) : (
          props.people.map((person) => (
            <Text size="xs" key={person.userId + person.slot}>
              {person.name}
              {person.isDelegate ? (
                <Text size="xs" tone="muted" as="span">
                  {' '}
                  ({t('massClr.submit.delegate')})
                </Text>
              ) : null}
            </Text>
          ))
        )}
      </VStack>
      <Text size="xs" tone="muted">
        {t('massClr.submit.eligibleAt', { count: props.people.length, band: props.bandLabel })}{' '}
        {props.description}
      </Text>
    </VStack>
  )
}

function buildDeviationGroups(lines: MassClrLine[]) {
  const groups = new Map<string, { count: number; exposureDelta: number }>()
  for (const line of lines) {
    if (!line.reasonForDeviation) continue
    const state = getMassClrLineState(line)
    if (state.bucket !== 'deviated') continue
    const reason = line.reasonForDeviation.trim()
    const group = groups.get(reason) ?? { count: 0, exposureDelta: 0 }
    group.count++
    group.exposureDelta += state.proposedCreditLimit! - Number(line.currentCreditLimit ?? 0)
    groups.set(reason, group)
  }
  return [...groups.entries()]
    .map(([reason, group]) => ({ reason, ...group }))
    .sort((a, b) => b.count - a.count)
}
